import {
  Badge,
  Button,
  Divider,
  Flex,
  Popover,
  Stack,
  Text,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { appVersionApi } from '@/entities/system/version';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ServiceVersion = {
  name: string;
  version: string;
};

function sortObjectFn(obj: Record<string, unknown> = {}): ServiceVersion[] {
  const entries = Object.entries(obj).map(([name, value]) => ({
    name,
    version: String(value),
  }));

  return entries.sort((a, b) => {
    if (a.name === 'pamir') return -1;
    if (b.name === 'pamir') return 1;

    return a.name.localeCompare(b.name);
  });
}

function VersionRow({ name, version }: ServiceVersion) {
  return (
    <Flex
      justify={'space-between'}
      align={'center'}
      gap={'sm'}
      wrap={'nowrap'}
      data-testid={`app-version-service-${name}`}
    >
      <Text c={'dimmed'} size={'sm'} inline truncate='end'>
        {name}
      </Text>
      <Badge variant={'light'} radius={'xl'} size={'sm'} className='shrink-0'>
        {version}
      </Badge>
    </Flex>
  );
}

export default function AppVersion() {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { data: servicesVersions } = useQuery({
    queryKey: ['app-version'],
    queryFn: () =>
      appVersionApi.getVersion().then((res) => sortObjectFn(res?.data)),
  });

  const [pamirVersion, ...otherVersions] = servicesVersions ?? [];
  const opened = hovered || pinned;

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    [],
  );

  if (!pamirVersion) {
    return null;
  }

  const openPopover = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setHovered(true);
  };

  const closePopover = () => {
    closeTimeoutRef.current = setTimeout(() => setHovered(false), 100);
  };

  const togglePinned = () => {
    if (pinned) {
      setPinned(false);
      setHovered(false);
      return;
    }

    setPinned(true);
  };

  const versionBadge = (
    <Badge
      variant='light'
      radius='xl'
      size='xs'
      className='shrink-0'
      data-testid='app-version-badge'
    >
      {pamirVersion.version}
    </Badge>
  );

  const versionButton = (
    <Button
      data-testid='app-version-button'
      variant='subtle'
      color='dark.3'
      size='compact-sm'
      fullWidth
      justify='space-between'
      pl='sm'
      pr={7}
      fw={400}
      styles={{ inner: { width: '100%' } }}
      rightSection={versionBadge}
      onClick={togglePinned}
      onMouseEnter={openPopover}
      onMouseLeave={closePopover}
      onFocus={openPopover}
      onBlur={closePopover}
    >
      <Text size='xs' c='dimmed'>
        {t('app.version')}
      </Text>
    </Button>
  );

  if (otherVersions.length === 0) {
    return versionButton;
  }

  return (
    <Popover
      opened={opened}
      onChange={(nextOpened) => {
        if (!nextOpened) {
          setHovered(false);
          setPinned(false);
        }
      }}
      position='top-end'
      offset={8}
      withArrow
      width={260}
      withinPortal
      shadow='xl'
    >
      <Popover.Target>{versionButton}</Popover.Target>
      <Popover.Dropdown
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
        onFocusCapture={openPopover}
        onBlurCapture={closePopover}
      >
        <Stack gap='xs' data-testid='app-versions-popover'>
          <Text size='xs' fw={600}>
            {t('app.versions')}
          </Text>
          <Divider />
          {otherVersions.map((serviceVersion) => (
            <VersionRow
              key={serviceVersion.name}
              name={serviceVersion.name}
              version={serviceVersion.version}
            />
          ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}